import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { GuidedLanguageRadios } from "./GuidedLanguageRadios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Calendar, Euro, Users, MapPin, Clock, CheckCircle, XCircle, CreditCard, Building2, Globe, Edit, Trash2, MoreVertical, Heart, Link2, AlertCircle, Plus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./components/ui/dropdown-menu";

// Helper function to strip HTML tags from text for preview display
const stripHtmlTags = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Event Booking Dialog Component
export const BookingDialog = ({ 
  isOpen, 
  onClose, 
  event, 
  bookingForm, 
  setBookingForm, 
  onSubmit, 
  onTicketQuantityChange,
  calculateTotal,
  editingBooking,
  currentUser, // Add currentUser prop for admin features
  renewMembership // Add renewal function prop
}) => {
  if (!event) return null;

  const isEditMode = !!editingBooking;
  
  // Check membership status for booking restrictions
  const isExpiredMember = currentUser && !currentUser.is_membership_active;
  const canRenew = currentUser && currentUser.can_renew;

  const formatDate = (dateString) => {
    if (!dateString) return "TBA";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Calculate total tickets from sophisticated structure or fallback to legacy
  const totalTickets = isEditMode 
    ? (editingBooking?.tickets?.current_user_tickets || 0) + 
      (editingBooking?.tickets?.family_members?.length || 0) + 
      (editingBooking?.tickets?.minor_tickets?.length || 0)
    : bookingForm.full_price_tickets + bookingForm.reduced_price_tickets;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isEditMode ? 'Edit Booking' : 'Book Event Tickets'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Event Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-600" />
                {event.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="h-4 w-4" />
                <span>{formatDate(event.event_date)}</span>
              </div>
              
              {event.venue_ids && event.venue_ids.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="h-4 w-4" />
                  <span>Event Venue</span>
                </div>
              )}
              
              {event.description_text && (
                <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                  {stripHtmlTags(event.description_text).substring(0, 200)}...
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Membership Status Check */}
          {isExpiredMember && !isEditMode && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-red-900 mb-2">
                      Membership Required
                    </h4>
                    <p className="text-sm text-red-700 mb-3">
                      Your membership has expired. You need an active membership to book events.
                    </p>
                    {canRenew ? (
                      <Button 
                        type="button"
                        size="sm" 
                        onClick={renewMembership}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Renew Membership €35
                      </Button>
                    ) : (
                      <p className="text-xs text-red-600">
                        Membership renewal is only available in December.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ticket Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-slate-600" />
                Select Tickets
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Full Price Tickets */}
              {event.cost_euro_full && event.cost_euro_full > 0 && (
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium">Full Price Ticket</h4>
                    <p className="text-sm text-slate-600">{formatCurrency(event.cost_euro_full)} per person</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label htmlFor="full_price_tickets" className="sr-only">Full Price Tickets</Label>
                    <Input
                      id="full_price_tickets"
                      type="number"
                      min="0"
                      max="10"
                      value={bookingForm.full_price_tickets}
                      onChange={(e) => onTicketQuantityChange('full_price_tickets', e.target.value)}
                      className="w-20 text-center"
                    />
                    <span className="text-sm text-slate-600">tickets</span>
                  </div>
                </div>
              )}

              {/* Reduced Price Tickets */}
              {event.cost_euro_reduced && event.cost_euro_reduced > 0 && (
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium">Reduced Price Ticket</h4>
                    <p className="text-sm text-slate-600">{formatCurrency(event.cost_euro_reduced)} per person</p>
                    <p className="text-xs text-slate-500">Students, seniors, groups</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label htmlFor="reduced_price_tickets" className="sr-only">Reduced Price Tickets</Label>
                    <Input
                      id="reduced_price_tickets"
                      type="number"
                      min="0"
                      max="10"
                      value={bookingForm.reduced_price_tickets}
                      onChange={(e) => onTicketQuantityChange('reduced_price_tickets', e.target.value)}
                      className="w-20 text-center"
                    />
                    <span className="text-sm text-slate-600">tickets</span>
                  </div>
                </div>
              )}

              {/* Total Tickets Display */}
              {totalTickets > 0 && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-blue-800">
                    Total: {totalTickets} ticket{totalTickets !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Guided Visit Language Selection - Only show if event offers guided visits */}
          {event.guided_visit && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="h-5 w-5 text-slate-600" />
                  Guided visit: please select your language
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="guided_visit_language">Language preference</Label>
                  <div className="mt-2">
                    <GuidedLanguageRadios
                      name="guided-language-booking"
                      value={bookingForm.guided_visit_language}
                      onChange={(value) => setBookingForm(prev => ({
                        ...prev,
                        guided_visit_language: value,
                        guided_visit_required: true // Always true when event has guided visits
                      }))}
                    />
                  </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                    As a free additional service, the ACE offers its members guided tours in English and Italian. Language preferences of the members will be taken into account where possible, subject to availability. By booking, participants accept that no claims for reimbursement can be made if their preferred language is not available.
                  </p>
              </CardContent>
            </Card>
          )}

          {/* Comment Field */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Additional Comments</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="booking_comment">Comment (Optional)</Label>
                <textarea
                  id="booking_comment"
                  value={bookingForm.comment}
                  onChange={(e) => setBookingForm(prev => ({
                    ...prev,
                    comment: e.target.value
                  }))}
                  placeholder="Add any special requests, dietary requirements, accessibility needs, or other comments..."
                  className="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical mt-2"
                  rows="4"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Let us know about any special requirements or questions about your booking.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Admin Comments Field - Only visible to administrators in edit mode */}
          {isEditMode && currentUser?.role === 'Administrator' && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-amber-600" />
                  Admin Comments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Label htmlFor="admin_comments">Internal Notes (Admin Only)</Label>
                  <textarea
                    id="admin_comments"
                    value={bookingForm.admin_comments || ''}
                    onChange={(e) => setBookingForm(prev => ({
                      ...prev,
                      admin_comments: e.target.value
                    }))}
                    placeholder="Add internal notes, special arrangements, payment notes, or other administrative comments..."
                    className="w-full min-h-[100px] px-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-vertical mt-2 bg-white"
                    rows="4"
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    <strong>Admin Only:</strong> These comments are only visible to administrators and will not be shared with the customer.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Booking Summary */}
          <Card className="bg-slate-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Euro className="h-5 w-5 text-slate-600" />
                Booking Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isEditMode ? (
                // Show sophisticated ticket breakdown for edit mode
                <>
                  {editingBooking?.tickets?.current_user_tickets > 0 && (
                    <div className="flex justify-between">
                      <span>User: {editingBooking.tickets.current_user_tickets}</span>
                      <span>{formatCurrency(editingBooking.tickets.current_user_price)}</span>
                    </div>
                  )}
                  {/* Family member pricing hidden per user request */}
                  {editingBooking?.tickets?.minor_tickets?.length > 0 && (
                    <div className="flex justify-between">
                      <span>Minors: {editingBooking.tickets.minor_tickets.length}</span>
                      <span>{formatCurrency(editingBooking.tickets.minor_total_price)}</span>
                    </div>
                  )}
                </>
              ) : (
                // Show legacy breakdown for new bookings
                <>
                  {bookingForm.full_price_tickets > 0 && (
                    <div className="flex justify-between">
                      <span>{bookingForm.full_price_tickets} × Full Price</span>
                      <span>{formatCurrency(event.cost_euro_full * bookingForm.full_price_tickets)}</span>
                    </div>
                  )}
                  
                  {bookingForm.reduced_price_tickets > 0 && (
                    <div className="flex justify-between">
                      <span>{bookingForm.reduced_price_tickets} × Reduced Price</span>
                      <span>{formatCurrency(event.cost_euro_reduced * bookingForm.reduced_price_tickets)}</span>
                    </div>
                  )}
                </>
              )}
              
              {totalTickets > 0 && (
                <>
                  <Separator />
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total Amount</span>
                    <span className="text-green-600">{formatCurrency(bookingForm.total_amount)}</span>
                  </div>
                </>
              )}

              {event.guided_visit && bookingForm.guided_visit_language && (
                <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                  ✓ Guided visit in {bookingForm.guided_visit_language} included
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card className="border-gray-200 bg-gray-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Edit className="h-4 w-4" />
                Comments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="booking_comments_simple">Additional comments or special requests</Label>
                <textarea
                  id="booking_comments_simple"
                  value={bookingForm.comments || ''}
                  onChange={(e) => setBookingForm({...bookingForm, comments: e.target.value})}
                  placeholder="Enter any additional comments, special requests, or notes for this booking..."
                  className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength="500"
                />
                <div className="text-xs text-gray-500 text-right">
                  {(bookingForm.comments || '').length}/500 characters
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={totalTickets === 0 || bookingForm.total_amount <= 0}
            >
              {isEditMode ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Update Booking ({formatCurrency(bookingForm.total_amount)})
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Proceed to Payment ({formatCurrency(bookingForm.total_amount)})
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// User Booking History Component
export const BookingHistory = ({ bookings, onClose, onDeleteBooking, onVerifyPayment }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      paid: { color: "bg-green-100 text-green-800", text: "Paid" },
      confirmed: { color: "bg-blue-100 text-blue-800", text: "Confirmed" },
      cancelled: { color: "bg-red-100 text-red-800", text: "Cancelled" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.color}>
        {config.text}
      </Badge>
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">My Booking History</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {bookings.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No bookings yet</h3>
              <p className="text-slate-500">Book your first cultural event to see it here!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{booking.event_title}</h3>
                        <p className="text-sm text-slate-600">
                          Event Date: {formatDate(booking.event_date)}
                        </p>
                        <p className="text-sm text-slate-600">
                          Booked: {formatDate(booking.booking_date)}
                        </p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(booking.payment_status)}
                        <p className="text-lg font-semibold text-green-600 mt-1">
                          {formatCurrency(booking.total_amount)}
                        </p>
                        
                        {/* User Action Buttons */}
                        <div className="flex items-center gap-2 mt-2">
                          {booking.payment_status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onVerifyPayment && onVerifyPayment(booking)}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              title="Refresh payment status if you completed payment"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Refresh Status
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Detailed Ticket Breakdown */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-medium text-slate-700 mb-3">Ticket Details</h4>
                      <div className="space-y-2 text-sm">
                        {/* Season pass → tier label, no per-event price. */}
                        {booking.source === "season_pass" ? (
                          <>
                            <div className="flex justify-between items-center">
                              <span>
                                {booking.season_pass_price_row_label || "Season pass"}: 1 × {formatCurrency(booking.total_amount)}
                                <span className="text-xs text-purple-700 italic ml-1">via season pass</span>
                              </span>
                              <span className="font-medium">{formatCurrency(booking.total_amount)}</span>
                            </div>
                            <div className="flex justify-between items-center font-medium text-blue-600 border-t pt-2 mt-2">
                              <span>Total: 1 tickets</span>
                              <span>{formatCurrency(booking.total_amount)}</span>
                            </div>
                          </>
                        ) : (booking.pricing_type === "custom_rows" && Array.isArray(booking.custom_row_selections) && booking.custom_row_selections.length > 0) ? (
                          <>
                            {booking.custom_row_selections.map((sel, idx) => (
                              <div key={idx} className="flex justify-between items-center">
                                <span>{sel.label || "Ticket"}: {sel.quantity} × {formatCurrency(sel.unit_price)}</span>
                                <span className="font-medium">{formatCurrency(sel.subtotal ?? (sel.quantity * (sel.unit_price || 0)))}</span>
                              </div>
                            ))}
                            {Array.isArray(booking.custom_row_minors) && booking.custom_row_minors.length > 0 && (
                              <div className="flex justify-between items-center">
                                <span>Minors: {booking.custom_row_minors.length}
                                  {" "}<span className="text-xs text-slate-500 ml-1">
                                    ({booking.custom_row_minors.map(m => `${m.first_name} ${m.last_name}`).join(", ")})
                                  </span>
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(booking.custom_row_minors.reduce((s, m) => s + (m.subtotal || 0), 0))}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between items-center font-medium text-blue-600 border-t pt-2 mt-2">
                              <span>Total: {booking.custom_row_selections.reduce((s, r) => s + (r.quantity || 0), 0) + (booking.custom_row_minors?.length || 0)} tickets</span>
                              <span>{formatCurrency(booking.total_amount)}</span>
                            </div>
                          </>
                        ) : booking.tickets ? (
                          <>
                            {booking.tickets?.current_user_tickets > 0 && (
                              <div className="flex justify-between items-center">
                                <span>User: {booking.tickets.current_user_tickets} × {formatCurrency(booking.tickets.current_user_price)}</span>
                                <span className="font-medium">{formatCurrency(booking.tickets.current_user_tickets * booking.tickets.current_user_price)}</span>
                              </div>
                            )}
                            {/* Family member pricing hidden per user request */}
                            {booking.tickets?.minor_tickets?.length > 0 && (
                              <div className="flex justify-between items-center">
                                <span>Minors: {booking.tickets.minor_tickets.length} × {formatCurrency(booking.tickets.minor_total_price / booking.tickets.minor_tickets.length)}</span>
                                <span className="font-medium">{formatCurrency(booking.tickets.minor_total_price)}</span>
                              </div>
                            )}
                            {booking.tickets?.guest_tickets?.length > 0 && (
                              <div className="flex justify-between items-center">
                                <span>{booking.tickets.guest_tickets.length > 1 ? 'Visiting family members' : 'Visiting family member'}: {booking.tickets.guest_tickets.length} × {formatCurrency(booking.tickets.guest_total_price / booking.tickets.guest_tickets.length)}
                                  <span className="text-xs text-slate-500 ml-1">({booking.tickets.guest_tickets.map(g => `${g.first_name} ${g.last_name}`).join(', ')})</span>
                                </span>
                                <span className="font-medium">{formatCurrency(booking.tickets.guest_total_price)}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center font-medium text-blue-600 border-t pt-2 mt-2">
                              <span>Total: {(booking.tickets?.current_user_tickets || 0) + (booking.tickets?.family_members?.length || 0) + (booking.tickets?.minor_tickets?.length || 0) + (booking.tickets?.guest_tickets?.length || 0)} tickets</span>
                              <span>{formatCurrency(booking.total_amount)}</span>
                            </div>
                          </>
                        ) : (
                          /* Fallback to legacy ticket display */
                          <>
                            {booking.full_price_tickets > 0 && (
                              <div className="flex justify-between items-center">
                                <span>Full Price: {booking.full_price_tickets} tickets</span>
                                <span className="font-medium">{formatCurrency(booking.full_price_total || (booking.full_price_tickets * (booking.total_amount / (booking.full_price_tickets + booking.reduced_price_tickets))))}</span>
                              </div>
                            )}
                            {booking.reduced_price_tickets > 0 && (
                              <div className="flex justify-between items-center">
                                <span>Reduced Price: {booking.reduced_price_tickets} tickets</span>
                                <span className="font-medium">{formatCurrency(booking.reduced_price_total || (booking.reduced_price_tickets * (booking.total_amount / (booking.full_price_tickets + booking.reduced_price_tickets))))}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center font-medium text-blue-600 border-t pt-2 mt-2">
                              <span>Total: {(booking.full_price_tickets || 0) + (booking.reduced_price_tickets || 0)} tickets</span>
                              <span>{formatCurrency(booking.total_amount)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Additional Booking Information */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4 pt-4 border-t">
                      {booking.guided_visit_required && (
                        <div>
                          <span className="font-medium text-slate-700">Guided Visit:</span>
                          <div className="mt-1">{booking.guided_visit_language || "Yes"}</div>
                        </div>
                      )}
                      
                      <div>
                        <span className="font-medium text-slate-700">Payment Method:</span>
                        <div className="mt-1">{booking.payment_method || "Stripe"}</div>
                      </div>
                      
                      <div>
                        <span className="font-medium text-slate-700">Booking ID:</span>
                        <div className="mt-1 text-xs font-mono">{booking.id?.slice(-8) || "N/A"}</div>
                      </div>
                    </div>
                    
                    {booking.payment_date && (
                      <div className="mt-3 pt-3 border-t text-sm text-slate-600">
                        Payment completed: {formatDate(booking.payment_date)}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} className="w-full sm:w-auto">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Admin Event Bookings Table Component
export const EventBookingsTable = ({ 
  isOpen = false,
  eventId, 
  eventTitle, 
  bookings = [], 
  stats, 
  isLoading = false,
  onClose,
  onEditBooking,
  onDeleteBooking,
  onUpdateBookingStatus,
  onCreateManualBooking,
  currentUser
}) => {
  const [showCancelled, setShowCancelled] = React.useState(false);
  const filteredBookings = bookings.filter(b => showCancelled || b.payment_status !== 'cancelled');
  const cancelledCount = bookings.filter(b => b.payment_status === 'cancelled').length;
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      paid: { color: "bg-green-100 text-green-800", text: "Paid" },
      confirmed: { color: "bg-blue-100 text-blue-800", text: "Confirmed" },
      cancelled: { color: "bg-red-100 text-red-800", text: "Cancelled" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.color}>
        {config.text}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl flex items-center justify-between gap-3 flex-wrap">
            <span>Event Bookings: {eventTitle}</span>
            {onCreateManualBooking && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 h-8 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                onClick={onCreateManualBooking}
                data-testid="open-manual-booking-from-table-btn"
                title="Create a manual booking and email a payment link"
              >
                <Plus className="h-3.5 w-3.5" />
                Manual booking
              </Button>
            )}
          </DialogTitle>
          {cancelledCount > 0 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-2" data-testid="toggle-cancelled-bookings">
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
                className="rounded"
              />
              <span className="text-slate-600">Show cancelled ({cancelledCount})</span>
            </label>
          )}
        </DialogHeader>
        
        {/* Booking Statistics */}
        {stats && (
          <Card className="mb-4 sm:mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg">Booking Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-blue-600">{stats.total_bookings}</div>
                  <div className="text-xs sm:text-sm text-slate-600">Total Bookings</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-green-600">{stats.total_tickets}</div>
                  <div className="text-xs sm:text-sm text-slate-600">Total Tickets</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-emerald-600">{formatCurrency(stats.total_revenue)}</div>
                  <div className="text-xs sm:text-sm text-slate-600">Total Revenue</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-slate-600">
                    {stats.max_capacity === 'Unlimited' ? '∞' : stats.max_capacity}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-600">Max Capacity</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Bookings Display - Mobile Cards + Desktop Table */}
        {isLoading && bookings.length === 0 ? (
          <div className="space-y-3 py-4" data-testid="event-bookings-loading-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-1/3 rounded bg-slate-200" />
                    <div className="h-2.5 w-1/2 rounded bg-slate-100" />
                  </div>
                  <div className="h-6 w-16 rounded bg-slate-100" />
                  <div className="h-6 w-20 rounded bg-slate-100" />
                </div>
              </div>
            ))}
            <div className="text-center text-xs text-slate-400 pt-1">Loading bookings…</div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No bookings yet</h3>
            <p className="text-slate-500">{cancelledCount > 0 ? 'All bookings are cancelled. Enable "Show cancelled" to view them.' : 'This event has no bookings.'}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {filteredBookings.map((booking) => (
                <Card key={booking.id} className="p-4">
                  <div className="space-y-3">
                    {/* Customer Info */}
                    <div className="border-b pb-2">
                      <div className="font-medium text-sm">{booking.user_name}</div>
                      <div className="text-xs text-slate-600">{booking.user_email}</div>
                      {booking.is_manual_booking && (
                        <div className="mt-1 flex flex-col gap-0.5">
                          <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[10px] w-fit">
                            Manual · {booking.booking_reason_display || booking.booking_reason}
                          </Badge>
                          {booking.created_by_admin && (
                            <span className="text-[10px] text-slate-500">
                              by {booking.created_by_admin}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Booking Details */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="font-medium text-slate-700">Tickets:</span>
                        <div className="mt-1">
                          {/* Season pass → tier label, no per-event price. */}
                          {booking.source === "season_pass" ? (
                            <>
                              <div>{booking.season_pass_price_row_label || "Season pass"}: 1 × {formatCurrency(booking.total_amount)}</div>
                              <div className="text-[10px] text-purple-700 italic">via season pass</div>
                              <div className="font-medium text-blue-600 border-t pt-1 mt-1">Total: 1 tickets</div>
                            </>
                          ) : (booking.pricing_type === "custom_rows" && Array.isArray(booking.custom_row_selections) && booking.custom_row_selections.length > 0) ? (
                            <>
                              {booking.custom_row_selections.map((sel, idx) => (
                                <div key={idx}>{sel.label || "Ticket"}: {sel.quantity} × {formatCurrency(sel.unit_price)}</div>
                              ))}
                              {Array.isArray(booking.custom_row_minors) && booking.custom_row_minors.length > 0 && (
                                <div>Minors: {booking.custom_row_minors.length}</div>
                              )}
                              <div className="font-medium text-blue-600 border-t pt-1 mt-1">
                                Total: {booking.custom_row_selections.reduce((s, r) => s + (r.quantity || 0), 0) + (booking.custom_row_minors?.length || 0)} tickets
                              </div>
                            </>
                          ) : (
                            <>
                              {booking.tickets?.current_user_tickets > 0 && (
                                <div>User: {booking.tickets.current_user_tickets} × {formatCurrency(booking.tickets.current_user_price)}</div>
                              )}
                              {booking.tickets?.minor_tickets?.length > 0 && (
                                <div>Minors: {booking.tickets.minor_tickets.length} × {formatCurrency(booking.tickets.minor_total_price / booking.tickets.minor_tickets.length)}</div>
                              )}
                              {booking.tickets?.guest_tickets?.length > 0 && (
                                <div>Visiting family members: {booking.tickets.guest_tickets.length} × {formatCurrency(booking.tickets.guest_total_price / booking.tickets.guest_tickets.length)}</div>
                              )}
                              <div className="font-medium text-blue-600 border-t pt-1 mt-1">
                                Total: {(booking.tickets?.current_user_tickets || 0) + (booking.tickets?.family_members?.length || 0) + (booking.tickets?.minor_tickets?.length || 0) + (booking.tickets?.guest_tickets?.length || 0)} tickets
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <span className="font-medium text-slate-700">Amount:</span>
                        <div className="mt-1 text-sm font-semibold text-green-600">
                          {formatCurrency(booking.total_amount)}
                        </div>
                      </div>
                      
                      <div>
                        <span className="font-medium text-slate-700">Status:</span>
                        <div className="mt-1">
                          {getStatusBadge(booking.payment_status)}
                        </div>
                      </div>
                      
                      <div>
                        <span className="font-medium text-slate-700">Guided Visit:</span>
                        <div className="mt-1">
                          {booking.guided_visit_required ? (
                            <div className="text-xs text-green-600">
                              <CheckCircle className="h-3 w-3 inline mr-1" />
                              {booking.guided_visit_language || "Yes"}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-500">
                              <XCircle className="h-3 w-3 inline mr-1" />
                              No
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <span className="font-medium text-slate-700">Dates:</span>
                        <div className="mt-1 text-xs">
                          <div>Booked: {formatDate(booking.booking_date)}</div>
                          <div>Paid: {booking.payment_date ? formatDate(booking.payment_date) : "Not paid"}</div>
                        </div>
                      </div>
                      
                      {/* User Comments */}
                      {(booking.comment || booking.notes) && (
                        <div className="col-span-2">
                          <span className="font-medium text-slate-700">Comment:</span>
                          <div className="mt-1 text-xs text-slate-600 bg-slate-50 p-2 rounded">
                            {booking.comment || booking.notes}
                          </div>
                        </div>
                      )}
                      
                      {/* Admin Comments - Only visible to admins */}
                      {currentUser?.role === 'Administrator' && booking.admin_comments && (
                        <div className="col-span-2">
                          <span className="font-medium text-amber-700">Admin Comments:</span>
                          <div className="mt-1 text-xs text-amber-800 bg-amber-50 p-2 rounded border border-amber-200">
                            {booking.admin_comments}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 text-xs h-8"
                        onClick={() => onUpdateBookingStatus && onUpdateBookingStatus(booking, booking.payment_status === 'paid' ? 'pending' : 'paid')}
                        title={`Mark as ${booking.payment_status === 'paid' ? 'Pending' : 'Paid'}`}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {booking.payment_status === 'paid' ? 'Pending' : 'Paid'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 text-xs h-8"
                        onClick={() => onEditBooking && onEditBooking(booking)}
                        title="Edit Booking"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs h-8 px-2"
                        onClick={() => onDeleteBooking && onDeleteBooking(booking)}
                        title="Delete Booking"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Customer</TableHead>
                    <TableHead className="min-w-[100px]">Tickets</TableHead>
                    <TableHead className="min-w-[80px]">Amount</TableHead>
                    <TableHead className="min-w-[100px]">Guided Visit</TableHead>
                    <TableHead className="min-w-[80px]">Status</TableHead>
                    <TableHead className="min-w-[100px]">Booking Date</TableHead>
                    <TableHead className="min-w-[100px]">Payment Date</TableHead>
                    <TableHead className="min-w-[120px]">Comments</TableHead>
                    {currentUser?.role === 'Administrator' && (
                      <TableHead className="min-w-[120px]">Admin Comments</TableHead>
                    )}
                    <TableHead className="min-w-[150px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{booking.user_name}</div>
                          <div className="text-sm text-slate-600">
                            {booking.user_email}
                            {booking.user_member_number ? <span className="text-slate-400"> · #{booking.user_member_number}</span> : null}
                          </div>
                          {booking.is_manual_booking && (
                            <div className="mt-1 flex flex-col gap-0.5">
                              <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[10px] w-fit">
                                Manual · {booking.booking_reason_display || booking.booking_reason}
                              </Badge>
                              {booking.created_by_admin && (
                                <span className="text-[10px] text-slate-500">
                                  by {booking.created_by_admin}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {/* Season-pass bookings: no per-event price, use the pass tier label. */}
                          {booking.source === "season_pass" ? (
                            <>
                              <div>
                                {booking.season_pass_price_row_label || "Season pass"}: 1 × {formatCurrency(booking.total_amount)}
                              </div>
                              <div className="text-[10px] text-purple-700 italic">via season pass</div>
                              <div className="font-medium border-t pt-1 mt-1">Total: 1 tickets</div>
                            </>
                          ) : (booking.pricing_type === "custom_rows" && Array.isArray(booking.custom_row_selections) && booking.custom_row_selections.length > 0) ? (
                            <>
                              {booking.custom_row_selections.map((sel, idx) => (
                                <div key={idx}>{sel.label || "Ticket"}: {sel.quantity} × {formatCurrency(sel.unit_price)}</div>
                              ))}
                              {Array.isArray(booking.custom_row_minors) && booking.custom_row_minors.length > 0 && (
                                <div>Minors: {booking.custom_row_minors.length}</div>
                              )}
                              <div className="font-medium border-t pt-1 mt-1">
                                Total: {booking.custom_row_selections.reduce((s, r) => s + (r.quantity || 0), 0) + (booking.custom_row_minors?.length || 0)} tickets
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Legacy / sophisticated ticket structure fallback */}
                              {booking.tickets?.current_user_tickets > 0 && (
                                <div>User: {booking.tickets.current_user_tickets} × {formatCurrency(booking.tickets.current_user_price)}</div>
                              )}
                              {booking.tickets?.minor_tickets?.length > 0 && (
                                <div>Minors: {booking.tickets.minor_tickets.length} × {formatCurrency(booking.tickets.minor_total_price / booking.tickets.minor_tickets.length)}</div>
                              )}
                              {booking.tickets?.guest_tickets?.length > 0 && (
                                <div>Visiting family members: {booking.tickets.guest_tickets.length} × {formatCurrency(booking.tickets.guest_total_price / booking.tickets.guest_tickets.length)}</div>
                              )}
                              <div className="font-medium border-t pt-1 mt-1">
                                Total: {(booking.tickets?.current_user_tickets || 0) + (booking.tickets?.family_members?.length || 0) + (booking.tickets?.minor_tickets?.length || 0) + (booking.tickets?.guest_tickets?.length || 0)} tickets
                              </div>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(booking.total_amount)}
                      </TableCell>
                      <TableCell>
                        {booking.guided_visit_required ? (
                          <div className="text-sm">
                            <CheckCircle className="h-4 w-4 text-green-600 inline mr-1" />
                            {booking.guided_visit_language || "Yes"}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-500">
                            <XCircle className="h-4 w-4 text-slate-400 inline mr-1" />
                            No
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(booking.payment_status)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(booking.booking_date)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {booking.payment_date ? formatDate(booking.payment_date) : "-"}
                      </TableCell>
                      <TableCell className="text-sm max-w-[120px]">
                        {(booking.comment || booking.notes) ? (
                          <div className="truncate bg-slate-50 p-1 rounded text-xs" title={booking.comment || booking.notes}>
                            {booking.comment || booking.notes}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {currentUser?.role === 'Administrator' && (
                        <TableCell className="text-sm max-w-[120px]">
                          {booking.admin_comments ? (
                            <div className="truncate bg-amber-50 p-1 rounded text-xs border border-amber-200 text-amber-800" title={booking.admin_comments}>
                              {booking.admin_comments}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onUpdateBookingStatus && onUpdateBookingStatus(booking, booking.payment_status === 'paid' ? 'pending' : 'paid')}
                            title={`Mark as ${booking.payment_status === 'paid' ? 'Pending' : 'Paid'}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onEditBooking && onEditBooking(booking)}
                            title="Edit Booking"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onDeleteBooking && onDeleteBooking(booking)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete Booking"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} className="w-full sm:w-auto">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Sophisticated Booking Dialog with Three Areas
export const SophisticatedBookingDialog = ({ 
  isOpen, 
  onClose, 
  event, 
  bookingForm, 
  setBookingForm, 
  onSubmit,
  currentUser,
  calculatePricing,
  onBookingForChange
}) => {
  // Always call hooks before any early returns
  const [pricingPreview, setPricingPreview] = React.useState(null);
  const [isCalculating, setIsCalculating] = React.useState(false);
  const [bookingForAnother, setBookingForAnother] = React.useState(false);
  const [searchedMember, setSearchedMember] = React.useState(null);
  const [remainingTickets, setRemainingTickets] = React.useState(null);
  const [onWaitlist, setOnWaitlist] = React.useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = React.useState(false);

  // Fetch remaining tickets when dialog opens
  React.useEffect(() => {
    if (!isOpen || !event?.id) return;
    const fetchAvailability = async () => {
      try {
        const res = await axios.get(`events/${event.id}/availability`);
        setRemainingTickets(res.data.remaining_tickets);
      } catch (err) {
        // Non-fatal: missing availability falls back to event capacity in the UI.
        if (process.env.NODE_ENV !== 'production') console.debug('availability fetch failed', err);
      }
    };
    const checkWaitlist = async () => {
      try {
        const res = await axios.get(`events/${event.id}/my-waitlist-status`);
        setOnWaitlist(res.data.on_waitlist);
      } catch (err) {
        // Non-fatal: 'on waitlist' badge stays hidden if the lookup fails.
        if (process.env.NODE_ENV !== 'production') console.debug('waitlist status fetch failed', err);
      }
    };
    fetchAvailability();
    checkWaitlist();
  }, [isOpen, event?.id]);
  
  // Calculate pricing whenever tickets or seat selections change
  React.useEffect(() => {
    const calculatePrices = async () => {
      // Early return if essential data is missing
      if (!event || !event.id || !bookingForm || !bookingForm.tickets) {
        console.log('Missing essential data for pricing calculation:', { 
          hasEvent: !!event, 
          hasEventId: !!event?.id, 
          hasBookingForm: !!bookingForm, 
          hasTickets: !!bookingForm?.tickets 
        });
        return;
      }
      
      const hasTickets = (bookingForm?.tickets?.current_user_tickets || 0) > 0 ||
                        (bookingForm?.linked_member_tickets || 0) > 0 ||
                        (bookingForm?.tickets?.minor_tickets || []).length > 0 ||
                        (bookingForm?.tickets?.family_members || []).length > 0 ||
                        (bookingForm?.tickets?.guest_tickets || []).length > 0;
      
      // Also trigger calculation if seat sections are selected (for pricing preview)
      const hasSeatSelections = bookingForm?.tickets?.current_user_seat_section ||
                               bookingForm?.tickets?.current_user_seat_type ||
                               bookingForm?.tickets?.minor_seat_section ||
                               bookingForm?.tickets?.minor_seat_type ||
                               bookingForm?.tickets?.family_seat_section ||
                               bookingForm?.tickets?.family_seat_type;
      
      console.log('🔍 Pricing calculation check:', {
        hasTickets,
        hasSeatSelections,
        current_user_tickets: bookingForm?.tickets?.current_user_tickets || 0,
        linked_member_tickets: bookingForm?.linked_member_tickets || 0,
        bookingForAnother: bookingForAnother,
        hasSearchedMember: !!searchedMember,
        booking_for_user_id: bookingForm?.booking_for_user_id,
        current_user_seat_section: bookingForm?.tickets?.current_user_seat_section || null,
        current_user_seat_type: bookingForm?.tickets?.current_user_seat_type || null,
        shouldCalculate: hasTickets || hasSeatSelections
      });
      
      if (!hasTickets && !hasSeatSelections) {
        console.log('❌ No calculation needed - no tickets and no seat selections');
        setPricingPreview(null);
        return;
      }

      console.log('✅ Starting pricing calculation...');
      setIsCalculating(true);
      try {
        console.log('📞 Calculating pricing for:', bookingForm.tickets); // Debug log
        console.log('   - Current user tickets:', bookingForm.tickets.current_user_tickets);
        console.log('   - Linked member tickets:', bookingForm.linked_member_tickets);
        console.log('   - Booking for another:', bookingForAnother);
        console.log('   - Searched member:', searchedMember);
        console.log('   - Booking for user ID:', bookingForm.booking_for_user_id);
        console.log('   - Minor tickets:', bookingForm.tickets.minor_tickets);
        console.log('   - Minor tickets count:', bookingForm.tickets.minor_tickets?.length);
        
        let pricing;
        
        // If booking for another member, we need to calculate both tickets separately
        // Check using booking_for_user_id instead of searchedMember to avoid scope issues
        const hasLinkedMemberBooking = bookingForm.booking_for_user_id && bookingForm.linked_member_tickets > 0;
        console.log('🔍 Checking dual calculation conditions:', {
          bookingForAnother,
          hasSearchedMember: !!searchedMember,
          booking_for_user_id: bookingForm.booking_for_user_id,
          linkedMemberTickets: bookingForm.linked_member_tickets,
          hasLinkedMemberBooking,
          shouldUseDualCalculation: hasLinkedMemberBooking
        });
        
        if (hasLinkedMemberBooking) {
          console.log('📞 Calculating for BOTH current user and linked member...');
          
          // Calculate current user's ticket + minors + guests (if any)
          let currentUserPricing = { current_user: { total: 0 }, minors: { total: 0 }, family_members: { total: 0 }, guests: { total: 0 }, total_amount: 0 };
          if (bookingForm.tickets.current_user_tickets > 0 || 
              (bookingForm.tickets.minor_tickets || []).length > 0 || 
              (bookingForm.tickets.guest_tickets || []).length > 0 ||
              (bookingForm.tickets.family_members || []).length > 0) {
            currentUserPricing = await calculatePricing(event.id, {
              current_user_tickets: bookingForm.tickets.current_user_tickets,
              current_user_seat_section: bookingForm.tickets.current_user_seat_section,
              current_user_seat_type: bookingForm.tickets.current_user_seat_type,
              minor_tickets: bookingForm.tickets.minor_tickets || [],
              minor_seat_section: bookingForm.tickets.minor_seat_section,
              family_members: bookingForm.tickets.family_members || [],
              family_seat_section: bookingForm.tickets.family_seat_section,
              guest_tickets: bookingForm.tickets.guest_tickets || [],
              guest_seat_section: bookingForm.tickets.guest_seat_section,
            }, null); // No booking_for_user_id for current user
            console.log('   - Current user pricing:', currentUserPricing);
          }
          
          // Calculate linked member's ticket
          const linkedMemberPricing = await calculatePricing(event.id, {
            current_user_tickets: bookingForm.linked_member_tickets,
            current_user_seat_section: bookingForm.tickets.current_user_seat_section,
            current_user_seat_type: bookingForm.tickets.current_user_seat_type
          }, bookingForm.booking_for_user_id); // Use linked member's ID for their pricing
          console.log('   - Linked member pricing:', linkedMemberPricing);
          
          // Combine the results but keep separate breakdown
          pricing = {
            current_user: {
              total: currentUserPricing.current_user.total, // Only YOUR ticket price
              tickets: currentUserPricing.current_user.tickets || 0,
              details: currentUserPricing.current_user.details || []
            },
            linked_member: {
              total: linkedMemberPricing.current_user.total, // The OTHER member's ticket price
              tickets: linkedMemberPricing.current_user.tickets || 0,
              details: linkedMemberPricing.current_user.details || [],
              name: searchedMember ? `${searchedMember.first_name_1} ${searchedMember.last_name}` : 'Other member'
            },
            minors: currentUserPricing.minors || { total: 0 },
            family_members: currentUserPricing.family_members || { total: 0 },
            guests: currentUserPricing.guests || { total: 0 },
            total_amount: currentUserPricing.total_amount + linkedMemberPricing.total_amount,
            grand_total: currentUserPricing.total_amount + linkedMemberPricing.total_amount
          };
        } else {
          // Regular booking (no linked member)
          pricing = await calculatePricing(event.id, bookingForm.tickets, bookingForm.booking_for_user_id);
        }
        
        console.log('✅ Pricing result:', pricing); // Debug log
        console.log('   - Booking for user ID:', bookingForm.booking_for_user_id);
        console.log('   - Current user total:', pricing?.current_user?.total);
        console.log('   - Minors total:', pricing?.minors?.total);
        console.log('   - Grand total:', pricing?.grand_total);
        setPricingPreview(pricing);
        setBookingForm(prev => ({
          ...prev,
          total_amount: pricing.total_amount
        }));
      } catch (error) {
        console.error('❌ Failed to calculate pricing:', error);
      } finally {
        setIsCalculating(false);
      }
    };

    calculatePrices();
  }, [
    bookingForm?.tickets, 
    bookingForm?.linked_member_tickets,
    bookingForm?.booking_for_user_id,
    bookingForAnother,
    searchedMember,
    event?.id,
    // Add specific dependencies to ensure nested changes are detected
    bookingForm?.tickets?.current_user_tickets,
    bookingForm?.tickets?.current_user_seat_section,
    bookingForm?.tickets?.current_user_seat_type,
    bookingForm?.tickets?.minor_seat_section,
    bookingForm?.tickets?.minor_seat_type,
    bookingForm?.tickets?.family_seat_section,
    bookingForm?.tickets?.family_seat_type,
    bookingForm?.tickets?.guest_tickets?.length,
    bookingForm?.tickets?.guest_seat_section
  ]);
  
  // Early return after ALL hooks are declared
  if (!event) return null;

  // Helper function to determine price category based on age
  const getPriceCategory = (age) => {
    if (age < 18) return "Minor Price";
    if (age < 26) return "Young Adult Price";
    if (age >= 65) return "Senior(over 65) Price";
    return "Member Price";
  };

  // Helper function to format age category labels consistently
  const formatAgeCategoryLabel = (ageCategory) => {
    if (!ageCategory) return "Member";
    const categoryMap = {
      "senior": "Senior(over 65)",
      "young_adult": "Under 26",
      "minor": "Under 18",
      "member": "Member"
    };
    return categoryMap[ageCategory.toLowerCase()] || ageCategory;
  };

  // Helper function to get current user's price category based on event pricing availability
  const getCurrentUserPriceCategory = () => {
    if (!currentUser?.birth_date || !event) return "Member Price";
    
    // Calculate age from birth_date
    const today = new Date();
    const birthDate = new Date(currentUser.birth_date.split('/').reverse().join('-')); // Convert DD/MM/YYYY to YYYY-MM-DD
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Determine what the user's age category would be
    let userAgeCategory = "member";
    if (age < 18) userAgeCategory = "minor";
    else if (age < 26) userAgeCategory = "young_adult"; 
    else if (age >= 65) userAgeCategory = "senior";
    
    // Check what pricing is actually enabled for this event
    const matrixPricing = event.matrix_pricing;
    const priceCategories = event.price_categories;
    
    if (matrixPricing) {
      // For matrix pricing events, check which categories are enabled
      if (userAgeCategory === "senior" && matrixPricing.senior_pricing_enabled) {
        return "Senior(over 65) Price";
      } else if (userAgeCategory === "minor" && matrixPricing.minor_pricing_enabled) {
        return "Minor Price";
      } else if (userAgeCategory === "young_adult" && matrixPricing.young_adult_pricing_enabled) {
        return "Young Adult Price";
      } else {
        // Fallback to member price if user's category is not enabled
        return "Member Price";
      }
    } else {
      // For legacy pricing, check which categories are enabled
      if (userAgeCategory === "senior" && priceCategories?.senior_price_enabled && priceCategories?.senior_price != null) {
        return "Senior(over 65) Price";
      } else if (userAgeCategory === "minor" && priceCategories?.minor_price_enabled && priceCategories?.minor_price != null) {
        return "Minor Price";
      } else if (userAgeCategory === "young_adult" && priceCategories?.young_adult_price_enabled && priceCategories?.young_adult_price != null) {
        return "Young Adult Price";
      } else {
        // Fallback to member price if user's category is not enabled
        return "Member Price";
      }
    }
  };

  // Helper function to get current user's price category with seat type
  const getCurrentUserPriceCategoryWithSeat = () => {
    const baseCategory = getCurrentUserPriceCategory();
    
    // Handle matrix pricing (Galleria/Platea)
    if (hasMatrixPricing && (bookingForm.tickets.current_user_seat_section || bookingForm.tickets.current_user_seat_type)) {
      const section = bookingForm.tickets.current_user_seat_section || bookingForm.tickets.current_user_seat_type;
      const userCategory = baseCategory.replace(' Price', '').toLowerCase();
      const price = formatMatrixPricing(userCategory, section);
      // Get custom section label
      const sectionLabel = section === "galleria" 
        ? (event.section_1_label || "Galleria") 
        : (event.section_2_label || "Platea");
      return `${sectionLabel} ${price}`;
    }
    
    // Handle traditional seat types
    if ((bookingForm.tickets.current_user_seat_section || bookingForm.tickets.current_user_seat_type) && (bookingForm.tickets.current_user_seat_section || bookingForm.tickets.current_user_seat_type) !== "none") {
      const selectedSeat = enabledSeatTypes.find(st => st.id === (bookingForm.tickets.current_user_seat_section || bookingForm.tickets.current_user_seat_type));
      if (selectedSeat) {
        return `${baseCategory} + ${selectedSeat.name} ${formatCurrency(selectedSeat.price)}`;
      }
    }
    
    return baseCategory;
  };

  // Helper function for minor tickets with seat type
  const getMinorPriceCategoryWithSeat = () => {
    const baseCategory = "Minor Price";
    
    // Handle matrix pricing (Galleria/Platea)
    if (hasMatrixPricing && (bookingForm.tickets.minor_seat_section || bookingForm.tickets.minor_seat_type)) {
      const section = bookingForm.tickets.minor_seat_section || bookingForm.tickets.minor_seat_type;
      const price = formatMatrixPricing("minor", section);
      // Get custom section label
      const sectionLabel = section === "galleria" 
        ? (event.section_1_label || "Galleria") 
        : (event.section_2_label || "Platea");
      return `${sectionLabel} ${price}`;
    }
    
    // Handle traditional seat types
    if ((bookingForm.tickets.minor_seat_section || bookingForm.tickets.minor_seat_type) && (bookingForm.tickets.minor_seat_section || bookingForm.tickets.minor_seat_type) !== "none") {
      const selectedSeat = enabledSeatTypes.find(st => st.id === (bookingForm.tickets.minor_seat_section || bookingForm.tickets.minor_seat_type));
      if (selectedSeat) {
        return `${baseCategory} + ${selectedSeat.name} ${formatCurrency(selectedSeat.price)}`;
      }
    }
    
    return baseCategory;
  };

  // Helper function for family members with seat type  
  const getFamilyMemberCategoryWithSeat = () => {
    let category = "Age-based + €20";
    
    // Handle matrix pricing (Galleria/Platea)
    if (hasMatrixPricing && (bookingForm.tickets.family_seat_section || bookingForm.tickets.family_seat_type)) {
      const section = bookingForm.tickets.family_seat_section || bookingForm.tickets.family_seat_type;
      const price = formatMatrixPricing("member", section);
      // Get custom section label
      const sectionLabel = section === "galleria" 
        ? (event.section_1_label || "Galleria") 
        : (event.section_2_label || "Platea");
      return `${sectionLabel} ${price} + €20`;
    }
    
    // Handle traditional seat types
    if ((bookingForm.tickets.family_seat_section || bookingForm.tickets.family_seat_type) && (bookingForm.tickets.family_seat_section || bookingForm.tickets.family_seat_type) !== "none") {
      const selectedSeat = enabledSeatTypes.find(st => st.id === (bookingForm.tickets.family_seat_section || bookingForm.tickets.family_seat_type));
      if (selectedSeat) {
        category += ` + ${selectedSeat.name} ${formatCurrency(selectedSeat.price)}`;
      }
    }
    
    return category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "TBA";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount || 0).toFixed(2)}`;
  };

  const updateTickets = (updates) => {
    setBookingForm(prev => ({
      ...prev,
      tickets: {
        ...prev.tickets || {},
        ...updates
      }
    }));
  };

  const addMinorTicket = () => {
    if (bookingForm.tickets.minor_tickets.length < 3) {
      updateTickets({
        minor_tickets: [...bookingForm.tickets.minor_tickets, {
          first_name: "",
          last_name: "",
          age: 16
        }]
      });
    }
  };

  const removeMinorTicket = (index) => {
    const newMinorTickets = [...bookingForm.tickets.minor_tickets];
    newMinorTickets.splice(index, 1);
    updateTickets({ minor_tickets: newMinorTickets });
  };

  const updateMinorTicket = (index, field, value) => {
    const newMinorTickets = [...bookingForm.tickets.minor_tickets];
    if (field === 'age') {
      newMinorTickets[index] = { ...newMinorTickets[index], age: parseInt(value) || 0 };
    } else {
      newMinorTickets[index] = { ...newMinorTickets[index], [field]: value };
    }
    updateTickets({ minor_tickets: newMinorTickets });
  };

  const addFamilyMember = () => {
    if (bookingForm.tickets.family_members.length < 2) {
      updateTickets({
        family_members: [...bookingForm.tickets.family_members, {
          first_name: "",
          last_name: "",
          age: 30
        }]
      });
    }
  };

  const removeFamilyMember = (index) => {
    const newFamilyMembers = [...bookingForm.tickets.family_members];
    newFamilyMembers.splice(index, 1);
    updateTickets({ family_members: newFamilyMembers });
  };

  const updateFamilyMember = (index, field, value) => {
    const newFamilyMembers = [...bookingForm.tickets.family_members];
    newFamilyMembers[index] = {
      ...newFamilyMembers[index],
      [field]: field === 'age' ? (parseInt(value) || 0) : value
    };
    updateTickets({ family_members: newFamilyMembers });
  };

  const getEnabledSeatTypes = () => {
    return event.seat_types?.filter(st => st.enabled) || [];
  };
  
  // Get matrix pricing sections (Galleria/Platea) if enabled
  const getMatrixPricingSections = () => {
    if (!event.matrix_pricing) return [];
    
    const sections = [];
    if (event.matrix_pricing.galleria_enabled) {
      sections.push({
        id: "galleria",
        name: event.section_1_label || "Galleria",  // Use custom label
        type: "matrix_section"
      });
    }
    if (event.matrix_pricing.platea_enabled) {
      sections.push({
        id: "platea", 
        name: event.section_2_label || "Platea",  // Use custom label
        type: "matrix_section"
      });
    }
    return sections;
  };

  const enabledSeatTypes = getEnabledSeatTypes();
  const matrixSections = getMatrixPricingSections();
  const hasMatrixPricing = matrixSections.length > 0;
  const hasSeatTypes = enabledSeatTypes.length > 0;
  
  // Helper function to get matrix pricing for a specific category and section
  const getMatrixPrice = (category, section) => {
    console.log('🔍 getMatrixPrice called:', { category, section });
    console.log('   Event has matrix_pricing:', !!event.matrix_pricing);
    console.log('   Event section_1_label:', event.section_1_label);
    console.log('   Event section_2_label:', event.section_2_label);
    
    if (!event.matrix_pricing || !hasMatrixPricing) {
      console.log('❌ No matrix_pricing found');
      return 0;
    }
    
    // Map category names to the correct pricing keys
    const categoryMap = {
      'member': 'member_pricing',
      'minor': 'minor_pricing', 
      'senior': 'senior_pricing',
      'young_adult': 'young_adult_pricing'
    };
    
    const categoryKey = categoryMap[category.toLowerCase()];
    console.log('   Looking for category:', categoryKey);
    console.log('   Available categories:', Object.keys(event.matrix_pricing));
    
    const categoryPricing = event.matrix_pricing[categoryKey];
    console.log('   Category pricing:', categoryPricing);
    
    if (!categoryKey || !categoryPricing) {
      console.log('❌ No pricing for category:', categoryKey);
      return 0;
    }
    
    // Build the price key
    const sectionKey = `${section}_price`;
    console.log('   Looking for section price:', sectionKey);
    
    const price = parseFloat(categoryPricing[sectionKey] || 0);
    console.log('   ✅ Price found:', price);
    
    return price;
  };
  
  // Helper function to format matrix pricing display
  const formatMatrixPricing = (category, section) => {
    const price = getMatrixPrice(category, section);
    return price > 0 ? `€${price.toFixed(2)}` : "Free";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">Book Event: {event.title}</DialogTitle>
          <p className="text-sm text-slate-600">{formatDate(event.event_date)}</p>
        </DialogHeader>

        {/* Low ticket warning */}
        {remainingTickets !== null && remainingTickets > 0 && remainingTickets <= 3 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3" data-testid="low-ticket-warning">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm font-semibold text-amber-800">
              Only {remainingTickets} ticket{remainingTickets !== 1 ? 's' : ''} remaining!
            </p>
          </div>
        )}

        {/* Sold out — waitlist */}
        {remainingTickets !== null && remainingTickets === 0 && (
          <div className="space-y-3" data-testid="sold-out-section">
            <div className="flex items-center gap-2 bg-red-50 border border-red-300 rounded-lg px-4 py-3">
              <XCircle className="h-5 w-5 text-red-600 shrink-0" />
              <p className="text-sm font-semibold text-red-800">This event is sold out.</p>
            </div>
            {onWaitlist ? (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="on-waitlist-msg">
                <Clock className="h-5 w-5 text-blue-600 shrink-0" />
                <p className="text-sm text-blue-800">You are on the waiting list. We will contact you if a spot opens up.</p>
              </div>
            ) : (
              <WaitlistForm
                event={event}
                currentUser={currentUser}
                onJoined={() => { setOnWaitlist(true); }}
                joiningWaitlist={joiningWaitlist}
                setJoiningWaitlist={setJoiningWaitlist}
              />
            )}
          </div>
        )}

        {/* Only show booking form if tickets are available */}
        {(remainingTickets === null || remainingTickets > 0) && (
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Area 1: Current User Tickets */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Your Ticket (Maximum 1)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Label className="min-w-20">Tickets:</Label>
                <Select
                  value={(bookingForm?.tickets?.current_user_tickets || 0).toString()}
                  onValueChange={(value) => updateTickets({
                    current_user_tickets: parseInt(value)
                  })}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(hasMatrixPricing || hasSeatTypes) && (bookingForm?.tickets?.current_user_tickets || 0) > 0 && (
                <div>
                  <Label className="mb-2 block">Select your seat:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {hasMatrixPricing ? (
                      matrixSections.map((section) => {
                        const userCategory = getCurrentUserPriceCategory().replace(' Price', '').toLowerCase();
                        const price = formatMatrixPricing(userCategory, section.id);
                        const isSelected = bookingForm?.tickets?.current_user_seat_section === section.id;
                        const isGalleria = section.id === 'galleria';
                        return (
                          <div
                            key={section.id}
                            onClick={() => updateTickets({ current_user_seat_section: section.id })}
                            className={`relative cursor-pointer rounded-xl border-2 p-4 text-center transition-all hover:shadow-md ${isSelected ? 'border-blue-600 bg-blue-50 shadow-md ring-2 ring-blue-300' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            data-testid={`seat-tile-member-${section.id}`}
                          >
                            {isSelected && <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-blue-600" />}
                            <div className="text-3xl mb-2">{isGalleria ? '🎭' : '🪑'}</div>
                            <div className={`font-semibold text-sm ${isSelected ? 'text-blue-700' : 'text-slate-800'}`}>{section.name}</div>
                            <div className={`text-lg font-bold mt-1 ${isSelected ? 'text-blue-600' : 'text-slate-600'}`}>{price}</div>
                          </div>
                        );
                      })
                    ) : (
                      enabledSeatTypes.map((seatType) => {
                        const isSelected = bookingForm?.tickets?.current_user_seat_type === seatType.id;
                        return (
                          <div
                            key={seatType.id}
                            onClick={() => updateTickets({ current_user_seat_type: seatType.id })}
                            className={`relative cursor-pointer rounded-xl border-2 p-4 text-center transition-all hover:shadow-md ${isSelected ? 'border-blue-600 bg-blue-50 shadow-md ring-2 ring-blue-300' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            data-testid={`seat-tile-member-${seatType.id}`}
                          >
                            {isSelected && <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-blue-600" />}
                            <div className="text-3xl mb-2">🪑</div>
                            <div className={`font-semibold text-sm ${isSelected ? 'text-blue-700' : 'text-slate-800'}`}>{seatType.name}</div>
                            <div className={`text-lg font-bold mt-1 ${isSelected ? 'text-blue-600' : 'text-slate-600'}`}>+{formatCurrency(seatType.price)}</div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {pricingPreview?.current_user?.total > 0 && (
                <div className="bg-white p-2 rounded border text-sm">
                  <strong>Your ticket: {formatCurrency(pricingPreview.current_user.total)}</strong>
                  <div className="text-xs text-blue-600 mt-1">
                    ({getCurrentUserPriceCategoryWithSeat()})
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Book for Family Member (Partner) Section */}
          <Card className="border-purple-200 bg-purple-50/30">
            <CardContent className="pt-6 space-y-4">
              <PartnerBookingSection
                currentUser={currentUser}
                bookingForm={bookingForm}
                setBookingForm={setBookingForm}
                pricingPreview={pricingPreview}
                onBookingForChange={onBookingForChange}
                bookingForAnother={bookingForAnother}
                setBookingForAnother={setBookingForAnother}
                searchedMember={searchedMember}
                setSearchedMember={setSearchedMember}
                formatCurrency={formatCurrency}
                formatAgeCategoryLabel={formatAgeCategoryLabel}
              />
            </CardContent>
          </Card>

          {/* Area 2: Minor Tickets */}
          <Card className="border-green-200 bg-green-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Minor Tickets for minors only accompanied by an ACE member (Maximum 3, Under 18)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {bookingForm.tickets.minor_tickets.map((minor, index) => (
                <div key={index} className="p-3 bg-white rounded border space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Minor {index + 1}</h4>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeMinorTicket(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor={`minor_${index}_first_name`}>First Name</Label>
                      <Input
                        id={`minor_${index}_first_name`}
                        value={minor.first_name || ''}
                        onChange={(e) => updateMinorTicket(index, 'first_name', e.target.value)}
                        placeholder="First name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`minor_${index}_last_name`}>Last Name</Label>
                      <Input
                        id={`minor_${index}_last_name`}
                        value={minor.last_name || ''}
                        onChange={(e) => updateMinorTicket(index, 'last_name', e.target.value)}
                        placeholder="Last name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`minor_${index}_age`}>Age</Label>
                      <Input
                        id={`minor_${index}_age`}
                        type="number"
                        min="1"
                        max="17"
                        value={minor.age || ''}
                        onChange={(e) => updateMinorTicket(index, 'age', e.target.value)}
                        placeholder="Age"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}

              {bookingForm.tickets.minor_tickets.length < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addMinorTicket}
                  className="w-full"
                >
                  Add Minor Ticket
                </Button>
              )}

              {(hasMatrixPricing || hasSeatTypes) && bookingForm.tickets.minor_tickets.length > 0 && (
                <div>
                  <Label className="mb-2 block">Seat for minors:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {hasMatrixPricing ? (
                      matrixSections.map((section) => {
                        const price = formatMatrixPricing("minor", section.id);
                        const isSelected = bookingForm.tickets.minor_seat_section === section.id;
                        const isGalleria = section.id === 'galleria';
                        return (
                          <div
                            key={section.id}
                            onClick={() => updateTickets({ minor_seat_section: section.id })}
                            className={`relative cursor-pointer rounded-xl border-2 p-3 text-center transition-all hover:shadow-md ${isSelected ? 'border-green-600 bg-green-50 shadow-md ring-2 ring-green-300' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            data-testid={`seat-tile-minor-${section.id}`}
                          >
                            {isSelected && <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-green-600" />}
                            <div className="text-2xl mb-1">{isGalleria ? '🎭' : '🪑'}</div>
                            <div className={`font-semibold text-sm ${isSelected ? 'text-green-700' : 'text-slate-800'}`}>{section.name}</div>
                            <div className={`text-base font-bold ${isSelected ? 'text-green-600' : 'text-slate-600'}`}>{price}</div>
                          </div>
                        );
                      })
                    ) : (
                      enabledSeatTypes.map((seatType) => {
                        const isSelected = bookingForm.tickets.minor_seat_type === seatType.id;
                        return (
                          <div
                            key={seatType.id}
                            onClick={() => updateTickets({ minor_seat_type: seatType.id })}
                            className={`relative cursor-pointer rounded-xl border-2 p-3 text-center transition-all hover:shadow-md ${isSelected ? 'border-green-600 bg-green-50 shadow-md ring-2 ring-green-300' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            data-testid={`seat-tile-minor-${seatType.id}`}
                          >
                            {isSelected && <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-green-600" />}
                            <div className="text-2xl mb-1">🪑</div>
                            <div className={`font-semibold text-sm ${isSelected ? 'text-green-700' : 'text-slate-800'}`}>{seatType.name}</div>
                            <div className={`text-base font-bold ${isSelected ? 'text-green-600' : 'text-slate-600'}`}>+{formatCurrency(seatType.price)}</div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {pricingPreview?.minors?.total > 0 && (
                <div className="bg-white p-2 rounded border text-sm">
                  <strong>Minor tickets total: {formatCurrency(pricingPreview.minors.total)}</strong>
                  <div className="text-xs text-green-600 mt-1">
                    ({bookingForm.tickets.minor_tickets.length} ticket{bookingForm.tickets.minor_tickets.length > 1 ? 's' : ''} - {getMinorPriceCategoryWithSeat()})
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Family Member Tickets section suppressed per user request */}

          {/* Guest Tickets — only when event allows guests */}
          {event.guest_tickets_enabled && (
          <Card className="border-orange-200 bg-orange-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Bring a Visiting Family Member (non-member, max 2)
                {event.guest_surcharge > 0 && (
                  <Badge variant="outline" className="text-xs text-orange-700 border-orange-300">
                    +{formatCurrency(event.guest_surcharge)} surcharge per visiting family member
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(bookingForm.tickets.guest_tickets || []).map((guest, index) => (
                <div key={index} className="p-3 bg-white rounded border space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Visiting Family Member {index + 1}</h4>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const updated = [...bookingForm.tickets.guest_tickets];
                        updated.splice(index, 1);
                        updateTickets({ guest_tickets: updated });
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={guest.first_name || ''}
                        onChange={(e) => {
                          const updated = [...bookingForm.tickets.guest_tickets];
                          updated[index] = { ...updated[index], first_name: e.target.value };
                          updateTickets({ guest_tickets: updated });
                        }}
                        placeholder="First name"
                        required
                        data-testid={`guest-${index}-first-name`}
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={guest.last_name || ''}
                        onChange={(e) => {
                          const updated = [...bookingForm.tickets.guest_tickets];
                          updated[index] = { ...updated[index], last_name: e.target.value };
                          updateTickets({ guest_tickets: updated });
                        }}
                        placeholder="Last name"
                        required
                        data-testid={`guest-${index}-last-name`}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={guest.email || ''}
                        onChange={(e) => {
                          const updated = [...bookingForm.tickets.guest_tickets];
                          updated[index] = { ...updated[index], email: e.target.value };
                          updateTickets({ guest_tickets: updated });
                        }}
                        placeholder="Email"
                        required
                        data-testid={`guest-${index}-email`}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {(bookingForm.tickets.guest_tickets || []).length < 2 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const updated = [...(bookingForm.tickets.guest_tickets || []), { first_name: '', last_name: '', email: '' }];
                    updateTickets({ guest_tickets: updated });
                  }}
                  className="w-full"
                  data-testid="add-guest-btn"
                >
                  Add Visiting Family Member Ticket
                </Button>
              )}

              {/* Guest seat section for matrix pricing */}
              {(hasMatrixPricing || hasSeatTypes) && (bookingForm.tickets.guest_tickets || []).length > 0 && (
                <div>
                  <Label className="mb-2 block">Seat for visiting family members:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {hasMatrixPricing ? (
                      matrixSections.map((section) => {
                        const isSelected = bookingForm.tickets.guest_seat_section === section.id;
                        const isGalleria = section.id === 'galleria';
                        return (
                          <div
                            key={section.id}
                            onClick={() => updateTickets({ guest_seat_section: section.id })}
                            className={`relative cursor-pointer rounded-xl border-2 p-3 text-center transition-all hover:shadow-md ${isSelected ? 'border-orange-600 bg-orange-50 shadow-md ring-2 ring-orange-300' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            data-testid={`seat-tile-guest-${section.id}`}
                          >
                            {isSelected && <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-orange-600" />}
                            <div className="text-2xl mb-1">{isGalleria ? '🎭' : '🪑'}</div>
                            <div className={`font-semibold text-sm ${isSelected ? 'text-orange-700' : 'text-slate-800'}`}>{section.name}</div>
                          </div>
                        );
                      })
                    ) : (
                      enabledSeatTypes.map((seatType) => {
                        const isSelected = bookingForm.tickets.guest_seat_section === seatType.id;
                        return (
                          <div
                            key={seatType.id}
                            onClick={() => updateTickets({ guest_seat_section: seatType.id })}
                            className={`relative cursor-pointer rounded-xl border-2 p-3 text-center transition-all hover:shadow-md ${isSelected ? 'border-orange-600 bg-orange-50 shadow-md ring-2 ring-orange-300' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            data-testid={`seat-tile-guest-${seatType.id}`}
                          >
                            {isSelected && <div className="absolute top-2 right-2 h-3 w-3 rounded-full bg-orange-600" />}
                            <div className="text-2xl mb-1">🪑</div>
                            <div className={`font-semibold text-sm ${isSelected ? 'text-orange-700' : 'text-slate-800'}`}>{seatType.name}</div>
                            <div className={`text-base font-bold ${isSelected ? 'text-orange-600' : 'text-slate-600'}`}>+{formatCurrency(seatType.price)}</div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {pricingPreview?.guests?.total > 0 && (
                <div className="bg-white p-2 rounded border text-sm">
                  <strong>Visiting family member tickets total: {formatCurrency(pricingPreview.guests.total)}</strong>
                  <div className="text-xs text-orange-600 mt-1">
                    ({(bookingForm.tickets.guest_tickets || []).length} visiting family member{(bookingForm.tickets.guest_tickets || []).length > 1 ? 's' : ''} - member price + {formatCurrency(event.guest_surcharge || 0)} surcharge each)
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Guided Visit Language Selection - Only show if event offers guided visits */}
          {event && event.guided_visit && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Guided visit: please select your language</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Language preference</Label>
                  <div className="mt-2">
                    <GuidedLanguageRadios
                      name="guided-language-sophisticated"
                      value={bookingForm.guided_visit_language}
                      onChange={(value) => setBookingForm(prev => ({
                        ...prev,
                        guided_visit_language: value,
                        guided_visit_required: true // Always true when event has guided visits
                      }))}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  As a free additional service, the ACE offers its members guided tours in English and Italian. Language preferences of the members will be taken into account where possible, subject to availability. By booking, participants accept that no claims for reimbursement can be made if their preferred language is not available.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Comments Section */}
          <Card className="border-gray-200 bg-gray-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Edit className="h-4 w-4" />
                Comments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="booking_comments">Additional comments or special requests</Label>
                <textarea
                  id="booking_comments"
                  value={bookingForm.tickets.comments || ''}
                  onChange={(e) => updateTickets({ comments: e.target.value })}
                  placeholder="Enter any additional comments, special requests, or notes for this booking..."
                  className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength="500"
                />
                <div className="text-xs text-gray-500 text-right">
                  {(bookingForm.tickets.comments || '').length}/500 characters
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total and Submit */}
          <Card className="bg-slate-50">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                {isCalculating ? (
                  <div className="text-lg">Calculating total...</div>
                ) : pricingPreview ? (
                  <>
                    <div className="text-2xl font-bold">
                      Total: {formatCurrency(pricingPreview.total_amount)}
                    </div>
                    {pricingPreview.total_amount > 0 && (
                      <div className="text-sm text-slate-600 space-y-1">
                        {pricingPreview.current_user.total > 0 && (
                          <div>Your ticket: {formatCurrency(pricingPreview.current_user.total)} <span className="text-blue-600">({getCurrentUserPriceCategoryWithSeat()})</span></div>
                        )}
                        {pricingPreview.linked_member?.total > 0 && (
                          <div>{pricingPreview.linked_member.name}'s ticket: {formatCurrency(pricingPreview.linked_member.total)} <span className="text-blue-600">({formatAgeCategoryLabel(pricingPreview.linked_member.details?.[0]?.age_category)})</span></div>
                        )}
                        {pricingPreview.minors.total > 0 && (
                          <div>Minor tickets: {formatCurrency(pricingPreview.minors.total)} <span className="text-green-600">({getMinorPriceCategoryWithSeat()})</span></div>
                        )}
                        {pricingPreview.family_members.total > 0 && (
                          <div>Family members: {formatCurrency(pricingPreview.family_members.total)} <span className="text-purple-600">({getFamilyMemberCategoryWithSeat()})</span></div>
                        )}
                        {pricingPreview.guests?.total > 0 && (
                          <div>Visiting family member tickets: {formatCurrency(pricingPreview.guests.total)} <span className="text-orange-600">(+{formatCurrency(event.guest_surcharge || 0)} surcharge each)</span></div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-lg">Select tickets to see total</div>
                )}

                <div className="flex gap-2 justify-center">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!pricingPreview || pricingPreview.total_amount <= 0 || isCalculating}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Proceed to Payment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Waitlist Form (shown when event is sold out) ──
const WaitlistForm = ({ event, currentUser, onJoined, joiningWaitlist, setJoiningWaitlist }) => {
  const [tickets, setTickets] = React.useState(1);
  const [partnerIncluded, setPartnerIncluded] = React.useState(false);
  const [minorCount, setMinorCount] = React.useState(0);

  const handleJoin = async () => {
    try {
      setJoiningWaitlist(true);
      const res = await axios.post(`events/${event.id}/waitlist`, {
        requested_tickets: tickets + (partnerIncluded ? 1 : 0) + minorCount,
        partner_included: partnerIncluded,
        minor_count: minorCount,
      });
      toast.success(`Added to waiting list (position #${res.data.position})`);
      onJoined();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join waiting list');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="waitlist-form">
      <CardContent className="pt-4 space-y-3">
        <p className="text-sm font-medium text-slate-700">Join the waiting list — no payment required</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">My ticket</Label>
            <Select value={tickets.toString()} onValueChange={v => setTickets(parseInt(v))}>
              <SelectTrigger className="h-8" data-testid="waitlist-tickets">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Partner</Label>
            <Select value={partnerIncluded ? "1" : "0"} onValueChange={v => setPartnerIncluded(v === "1")}>
              <SelectTrigger className="h-8" data-testid="waitlist-partner">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No</SelectItem>
                <SelectItem value="1">Yes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Minors</Label>
            <Select value={minorCount.toString()} onValueChange={v => setMinorCount(parseInt(v))}>
              <SelectTrigger className="h-8" data-testid="waitlist-minors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0,1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Total requested: {tickets + (partnerIncluded ? 1 : 0) + minorCount} ticket{(tickets + (partnerIncluded ? 1 : 0) + minorCount) !== 1 ? 's' : ''}
        </p>
        <Button
          className="w-full"
          onClick={handleJoin}
          disabled={joiningWaitlist}
          data-testid="join-waitlist-btn"
        >
          <Clock className="h-4 w-4 mr-2" />
          {joiningWaitlist ? 'Joining...' : 'Join Waiting List'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Partner Booking Section ──
// Replaces the old open-search "Book for Another Member" with partner-only linking
const PartnerBookingSection = ({
  currentUser,
  bookingForm,
  setBookingForm,
  pricingPreview,
  onBookingForChange,
  bookingForAnother,
  setBookingForAnother,
  searchedMember,
  setSearchedMember,
  formatCurrency,
  formatAgeCategoryLabel,
}) => {
  const [partner, setPartner] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [noPartner, setNoPartner] = React.useState(false);

  // Fetch partner on mount
  React.useEffect(() => {
    const fetchPartner = async () => {
      try {
        setLoading(true);
        const res = await axios.get('/users/my-partner');
        if (res.data?.partner) {
          setPartner(res.data.partner);
          setNoPartner(false);
        } else {
          setPartner(null);
          setNoPartner(true);
        }
      } catch {
        setPartner(null);
        setNoPartner(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPartner();
  }, []);

  const handleToggle = (checked) => {
    setBookingForAnother(checked);
    if (checked && partner) {
      // Auto-select partner as the searched member and default to 1 ticket
      setSearchedMember(partner);
      if (onBookingForChange) onBookingForChange(partner);
      setBookingForm(prev => ({
        ...prev,
        booking_for_user_id: partner.id,
        linked_member_tickets: prev?.linked_member_tickets > 0 ? prev.linked_member_tickets : 1,
      }));
    }
    if (!checked) {
      setSearchedMember(null);
      setBookingForm(prev => ({
        ...prev,
        tickets: { ...prev.tickets, current_user_tickets: 0 },
        booking_for_user_id: null,
        linked_member_tickets: 0,
      }));
    }
  };

  const calculateAge = (birthDate) => {
    try {
      let bd;
      if (typeof birthDate === 'string' && birthDate.includes('/')) {
        const parts = birthDate.split('/');
        if (parts.length === 3) bd = new Date(parts[2], parts[1] - 1, parts[0]);
      } else {
        bd = new Date(birthDate);
      }
      if (!bd || isNaN(bd.getTime())) return 'Unknown';
      const today = new Date();
      let age = today.getFullYear() - bd.getFullYear();
      const m = today.getMonth() - bd.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
      return (age >= 0 && age <= 120) ? age : 'Unknown';
    } catch { return 'Unknown'; }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-2">Loading family member info...</div>;
  }

  return (
    <>
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="bookingForPartner"
          checked={bookingForAnother}
          onChange={(e) => handleToggle(e.target.checked)}
          disabled={noPartner}
          className="w-4 h-4 rounded border-slate-300"
          data-testid="booking-for-partner-checkbox"
        />
        <Label htmlFor="bookingForPartner" className={`cursor-pointer font-medium ${noPartner ? 'text-slate-400' : ''}`}>
          <Heart className="h-4 w-4 inline mr-1 text-purple-500" />
          Also book for my partner / family member
        </Label>
      </div>

      {noPartner && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 ml-7" data-testid="no-partner-message">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">No family member linked</p>
            <p className="text-xs mt-1">
              To book for a family member, first link their account from your profile menu (click your name at the top right and select "Link partner / ACE member").
            </p>
          </div>
        </div>
      )}

      {bookingForAnother && partner && (
        <div className="space-y-3 pl-7">
          <div className="bg-green-50 border border-green-200 rounded-md p-3" data-testid="partner-info-card">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-green-700" />
              <p className="text-sm font-medium text-green-900">
                Booking for: {partner.first_name_1} {partner.last_name}
              </p>
            </div>
            <p className="text-xs text-green-700 mt-1">Email: {partner.email}</p>
            {partner.birth_date && (
              <p className="text-xs text-slate-600 mt-1">
                Age: {calculateAge(partner.birth_date)} years old
              </p>
            )}
            <p className="text-xs text-slate-600 mt-2">
              You will pay for this booking. The ticket will be registered under their name.
            </p>
          </div>

          {/* Ticket selection for partner */}
          <div>
            <Label>Select ticket for {partner.first_name_1}</Label>
            <Select
              value={(bookingForm?.linked_member_tickets || 0).toString()}
              onValueChange={(value) => {
                const ticketCount = parseInt(value) || 0;
                setBookingForm(prev => ({
                  ...prev,
                  linked_member_tickets: ticketCount,
                  booking_for_user_id: ticketCount > 0 ? partner.id : null,
                }));
              }}
            >
              <SelectTrigger className="w-full" data-testid="partner-ticket-select">
                <SelectValue placeholder="Select tickets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0 tickets</SelectItem>
                <SelectItem value="1">1 ticket</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Show partner's ticket price */}
          {bookingForm?.linked_member_tickets > 0 && pricingPreview?.linked_member?.total > 0 && (
            <div className="bg-white p-3 rounded border" data-testid="partner-price-preview">
              <div className="text-sm font-medium">
                {partner.first_name_1}'s ticket: {formatCurrency(pricingPreview.linked_member.total)}
              </div>
              <div className="text-xs text-blue-600 mt-1">
                Price category based on their age: {formatAgeCategoryLabel(pricingPreview.linked_member.details?.[0]?.age_category)}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};